import { useEffect, useState } from "react";
import { selectAuthorizedDistricts, selectUser } from "../../../context/slices/userProfileSlice";
import { useXNGDispatch, useXNGSelector } from "../../../context/store";
import { placeholderForFutureLogErrorText } from "../../../temp/errorText";
import { signAllElectronicSignatures } from "../../../utils/signElectronicSignature";
import {
  SchoolCampusRef,
  ServiceProviderResponse,
  ServiceProviderType,
  ServiceProviderTypesResponse,
} from "../../../profile-sdk";
import {
  API_SERVICEPROVIDERS_BACKEND,
  API_STATESNAPSHOTS,
  API_CAMPUSES_BACKEND,
} from "../../../api/api";
import { fetchServiceProviderResponse } from "../../../api/fetchServiceProviderResponse";
import { UserStatus } from "@xng/xng-backend";
import { AccountInfo } from "@azure/msal-browser";
import { FormFourValues, FormOneValues, FormThreeValues } from "./types";
import { FifthView, FirstView, FourthView, ThirdView, ZerothView } from "./views";
import { XNGSlideshow } from "../../../design/components-dev/slideshow";
import {
  STATEMENT_ELECTRONIC_SIGNATURE_CONSENT,
  STATEMENT_FERPA_AUTHORIZATION,
  STATEMENT_TRUE_AND_ACCURATE_DATA_AUTHORIZATION,
} from "./statements";
import { selectClient, selectClientID } from "../../../context/slices/loggedInClientSlice";
import { useNavigate } from "react-router";
import { setDataEntryProvider } from "../../../context/slices/dataEntryProvider";

interface IUserOnboarding {
  account: AccountInfo;
}
function UserOnboarding(props: IUserOnboarding) {
  if (props.account.idTokenClaims === undefined) throw new Error(placeholderForFutureLogErrorText);
  if (props.account.idTokenClaims.oid === undefined)
    throw new Error(placeholderForFutureLogErrorText);

  // ---------------------- ACCESSOR CONSTANTS ----------------------
  const _state: string = props.account.idTokenClaims.state as string;
  const _oid: string = props.account.idTokenClaims.oid;
  const _fullName =
    props.account.idTokenClaims.given_name + " " + props.account.idTokenClaims.family_name;

  // ---------------------- HOOKS ----------------------
  const navigate = useNavigate();

  // ---------------------- API ----------------------
  // --- SELECTORS
  const authorizedDistricts = useXNGSelector(selectAuthorizedDistricts);
  const client = useXNGSelector(selectClient);
  const user = useXNGSelector(selectUser);
  const dispatch = useXNGDispatch();
  if (client === null) throw new Error(placeholderForFutureLogErrorText);

  // --- API
  async function fetchAndSetState() {
    // Get school campuses for all authorized districts
    if (authorizedDistricts === null) throw new Error(placeholderForFutureLogErrorText);
    const districtIds = authorizedDistricts
      .map((d) => d.id)
      .filter((id): id is string => id !== undefined);
    
    const campusesResponse = await API_CAMPUSES_BACKEND.query({
      campusQueryBody: {
        districtIds,
      },
    });
    const dropdownRes: SchoolCampusRef[] = campusesResponse.map((c) => ({ id: c.id, name: c.name }));

    setSchoolCampuses(dropdownRes);
    // if therapist, but can skip step
    const serviceProviderTypeDropdown: ServiceProviderTypesResponse =
      await API_STATESNAPSHOTS.v1StateSnapshotsByDateServiceProviderTypesGet(_state, undefined);

    const dropdownOptions: ServiceProviderType[] | undefined =
      serviceProviderTypeDropdown.serviceProviderTypes;

    if (dropdownOptions === undefined) throw new Error(placeholderForFutureLogErrorText);

    setServiceProviderTypes(dropdownOptions);
  }
  useEffect(() => {
    fetchAndSetState();
  }, []);

  // ---------------------- STATES ----------------------
  // const [ipAddress, setIpAddress] = useState<string>("");
  const [schoolCampuses, setSchoolCampuses] = useState<SchoolCampusRef[]>([]);
  const [serviceProviderTypes, setServiceProviderTypes] = useState<ServiceProviderType[]>([]);
  const [slideView, setSlideView] = useState<number>(0);

  const [cachedFormOneValues, setCachedFormOneValues] = useState<FormOneValues | null>(null);
  const [cachedFormThreeValues, setCachedFormThreeValues] = useState<FormThreeValues | null>(null);
  // ---------------------- SUBMIT ----------------------
  async function handleFullSubmission() {
    // Create SP creation request
    if (cachedFormOneValues === null) throw new Error(placeholderForFutureLogErrorText);

    const spRecord = await API_SERVICEPROVIDERS_BACKEND.postServiceProviders({
      userId: _oid,
      postServiceProvidersRequestProviderProfile: {
        jobTitle: cachedFormOneValues.jobTitle,
        npi: parseInt(cachedFormOneValues.npi) as unknown as string, // the types in both the old and new api require a string, but the database wants an int
        stateMedicaidNumber: cachedFormOneValues.stateMedicaidNumber,
        status: UserStatus.NUMBER_1, // active — ensures the user can login
      },
    });

    if (spRecord.id === undefined) throw new Error(placeholderForFutureLogErrorText);

    const spResponse = await fetchServiceProviderResponse(spRecord.id);
    dispatch(setDataEntryProvider(spResponse));

    if (cachedFormThreeValues !== null) {
      // Post credential (therapist license data)
      await API_SERVICEPROVIDERS_BACKEND.postCredentials({
        serviceProviderId: spRecord.id,
        postCredentialsBody: {
          nameOnLicense: cachedFormThreeValues.nameOnLicense,
          licenseNumber: cachedFormThreeValues.licenseNumber,
          endDate: cachedFormThreeValues.licenseExpirationDate,
        },
      });
    }

    // Submit electronic signatures
    const signatureData = {
      oid: _oid,
      ipAddress: "",
      fullName: _fullName,
      state: _state,
    };
    await signAllElectronicSignatures(signatureData);
  }

  // ---------------------- DOM ----------------------
  return (
    <XNGSlideshow
      view={slideView}
      slides={[
        {
          id: 0,
          content: <ZerothView onNext={() => setSlideView(1)} />,
        },
        {
          id: 1,
          content: (
            <FirstView
              onValidNext={(values: FormOneValues) => {
                // Handle values...
                setCachedFormOneValues(values);
                setSlideView(3);
              }}
              apiDependentValues={{ campusDropdownOptions: schoolCampuses }}
            />
          ),
        },
        {
          id: 3,
          content: (
            <ThirdView
              apiDependentValues={{ licenseTypeDropdownOptions: serviceProviderTypes }}
              onSkip={() => setSlideView(4)}
              onValidNext={(values: FormThreeValues) => {
                // Handle values...
                setCachedFormThreeValues(values);
                setSlideView(4);
              }}
            />
          ),
        },
        {
          id: 4,
          content: (
            <FourthView
              onValidNext={async (values: FormFourValues) => {
                // Handle values...
                await handleFullSubmission();
                setSlideView(5);
              }}
              apiDependentValues={{
                ferpaAuthorizationStatement: STATEMENT_FERPA_AUTHORIZATION,
                electronicSignatureConsent: STATEMENT_ELECTRONIC_SIGNATURE_CONSENT,
                trueAndAccurateDataAuthorization: STATEMENT_TRUE_AND_ACCURATE_DATA_AUTHORIZATION,
              }}
            />
          ),
        },
        {
          id: 5,
          content: <FifthView onNext={() => navigate(0)} />,
        },
      ]}
    />
  );
}

export default UserOnboarding;
